import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { poolAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Plus, Users, DollarSign, Calendar, Trophy, Lock } from 'lucide-react';

export default function PoolDirectory() {
  const [pools, setPools] = useState([]);
  useEffect(() => { document.title = "My Pools — Pools FC"; }, []);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check for post-registration intent redirect
  useEffect(() => {
    const intent = localStorage.getItem('pfc_intent');
    const selectedPlan = localStorage.getItem('pfc_selected_plan');
    const selectedPool = localStorage.getItem('pfc_selected_pool');
    if (intent) {
      localStorage.removeItem('pfc_intent');
      localStorage.removeItem('pfc_selected_plan');
      localStorage.removeItem('pfc_selected_pool');
      if (intent === 'start_pool') {
        navigate(`/create-pool${selectedPlan ? `?plan=${selectedPlan}` : ''}`, { replace: true });
        return;
      }
      if (intent === 'join_pool' && selectedPool) {
        navigate(`/pools/${selectedPool}/join`, { replace: true });
        return;
      }
    }
  }, [navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await poolAPI.list({ search: search || undefined });
        // Sort: member pools first, then by creation date
        const sorted = res.data.pools.sort((a, b) => {
          if (a.is_member && !b.is_member) return -1;
          if (!a.is_member && b.is_member) return 1;
          return 0;
        });
        setPools(sorted);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [search]);

  const statusColors = { open: 'bg-green-100 text-green-800', locked: 'bg-yellow-100 text-yellow-800', completed: 'bg-blue-100 text-blue-800' };

  return (
    <div>
      <div className="relative overflow-hidden rounded-2xl border bg-card px-6 py-8 mb-6 noise-soft">
        <div className="absolute inset-0 opacity-[0.06] blur-[1px]" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1550591927-391fccf9934b?w=1200&q=30)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">Premier League Pools</h1>
          <p className="text-muted-foreground text-base max-w-xl">Browse pools, join one, pick your teams, and climb the leaderboard.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="pool-search" placeholder="Search pools..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex gap-2">
          {(user?.role === 'admin' || user?.role === 'organizer') && (
            <Button asChild><Link to="/create-pool"><Plus className="h-4 w-4 mr-1" />Create Pool</Link></Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={`skel-${i}`} className="h-48 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : pools.length === 0 ? (
        <div className="text-center py-16">
          <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-lg font-semibold mb-1">No Pools Found</h3>
          <p className="text-muted-foreground text-sm">{search ? 'Try a different search.' : 'No pools created yet.'}</p>
        </div>
      ) : (
        <div>
          {/* My Pools Section */}
          {pools.some(p => p.is_member) && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-semibold mb-3 text-[#5b3cc4]">My Pools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pool-grid">
                {pools.filter(p => p.is_member).map((pool) => (
                  <Link to={`/pools/${pool.id}`} key={pool.id}>
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full border-2 border-[#5b3cc4]/40 bg-[#f3f0ff]/20 ring-1 ring-[#5b3cc4]/10">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <CardTitle className="font-heading text-lg">{pool.name}</CardTitle>
                          <Badge className={statusColors[pool.status] || 'bg-gray-100'}>{pool.status}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{pool.entry_count || 0} entries</span>
                          <span className="flex items-center gap-1">Entry: {pool.organizer_fee || "Details in pool"}</span>
                          {pool.entry_deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(pool.entry_deadline).toLocaleDateString()}</span>}
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-muted-foreground">by {pool.organizer_name}</span>
                          <Badge className="bg-[#f3f0ff] text-[#5b3cc4] text-[10px]">Member</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Other Pools Section */}
          {pools.some(p => !p.is_member) && (
            <div>
              <h2 className="font-heading text-lg font-semibold mb-3 text-muted-foreground">Other Pools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pools.filter(p => !p.is_member).map((pool) => (
                  <Link to={`/pools/${pool.id}/join`} key={pool.id}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <CardTitle className="font-heading text-lg">{pool.name}</CardTitle>
                          <Badge className={statusColors[pool.status] || 'bg-gray-100'}>{pool.status}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{pool.entry_count || 0} entries</span>
                          <span className="flex items-center gap-1">Entry: {pool.organizer_fee || "Details in pool"}</span>
                          {pool.entry_deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(pool.entry_deadline).toLocaleDateString()}</span>}
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-muted-foreground">by {pool.organizer_name}</span>
                          <span className="text-xs text-[#5b3cc4] font-medium flex items-center gap-1"><Lock className="h-3 w-3" />Join to see details</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
